/**
 * province-seo.js — hand-curated per-province SEO data used by the
 * PropertyManagementProvince page and the AEO answer surface.
 *
 * Each entry ships:
 *   - top_cities: 6-10 real city slugs so we can render internal-link
 *                 anchor lists (huge Google ranking signal for province+city
 *                 head terms like "property management Halifax NS").
 *   - board:      the provincial regulator (LTB, RTB, TAL, RTDRS, IRAC…)
 *   - notice_days: the standard tenant notice-to-vacate period, used in
 *                 the Direct Answer copy.
 *   - avg_rent:   representative one-bedroom average rent in CAD (2026
 *                 CMHC + regional PMS data) — cited in the LocalBusiness
 *                 schema priceRange hint.
 *   - unique_hook: a one-sentence localised value prop used above the
 *                 fold to avoid duplicate-content flags from Google.
 *   - faq:        3 province-specific Q&As emitted as FAQPage JSON-LD.
 *
 * Keeping this data client-side means the SEO surface renders instantly
 * (no fetch waterfall) and the crawler sees fully-rendered HTML on first
 * paint via the /api/bots/markdown snapshot endpoint.
 */
export const PROVINCE_SEO_DATA = {
  "ontario": {
    top_cities: ["toronto", "ottawa", "mississauga", "hamilton", "london", "brampton", "markham", "kitchener", "windsor", "kingston"],
    board: "Landlord and Tenant Board (LTB)",
    notice_days: 60,
    avg_rent: 2350,
    unique_hook: "Ontario landlords rely on Central Rentals for LTB-ready N4, L1, and N5 forms with automatic timelines that stay on the safe side of the RTA.",
    faq: [
      {
        q: "How much notice do I need to give an Ontario tenant to end the lease?",
        a: "Ontario landlords must serve a Form N12 with at least 60 days' notice for personal use, and 60 days for termination at end of term. Central Rentals auto-generates the correct form and next-step timeline the moment you flag a unit."
      },
      {
        q: "Can I raise rent in Ontario in 2026?",
        a: "Yes — the Ontario 2026 rent-increase guideline is 2.5%. You must serve a Form N1 at least 90 days before the increase takes effect, and only once per 12-month period. Central Rentals ships the N1 pre-filled."
      },
      {
        q: "Who regulates landlord-tenant disputes in Ontario?",
        a: "The Landlord and Tenant Board (LTB), part of Tribunals Ontario, hears every residential tenancy dispute in the province. Central Rentals keeps an LTB-admissible audit trail of every notice, message, and payment."
      }
    ]
  },
  "british-columbia": {
    top_cities: ["vancouver", "surrey", "burnaby", "victoria", "richmond", "coquitlam", "kelowna", "abbotsford", "nanaimo", "kamloops"],
    board: "Residential Tenancy Branch (RTB)",
    notice_days: 30,
    avg_rent: 2600,
    unique_hook: "BC landlords use Central Rentals for RTB-compliant Form K move-in inspections and the correct 10-Day / 1-Month notices with sane defaults.",
    faq: [
      { q: "How do I evict a tenant in BC?", a: "You must serve the correct notice — 10 Day Notice for unpaid rent, One Month Notice for cause, or Two Month Notice for landlord use — and file through the RTB Dispute Resolution portal if the tenant disputes. Central Rentals ships the forms pre-filled." },
      { q: "What's the 2026 BC rent-increase limit?", a: "3.0% for 2026. You must give at least 3 months written notice using RTB-8 and it can only be applied once every 12 months per unit." },
      { q: "Does BC require a move-in condition inspection?", a: "Yes — the Form K condition inspection is mandatory at both move-in and move-out. Skipping it forfeits your right to withhold the damage deposit." }
    ]
  },
  "alberta": {
    top_cities: ["calgary", "edmonton", "red-deer", "lethbridge", "medicine-hat", "fort-mcmurray", "airdrie", "spruce-grove"],
    board: "Residential Tenancy Dispute Resolution Service (RTDRS)",
    notice_days: 90,
    avg_rent: 1700,
    unique_hook: "Alberta owner-operators run Central Rentals to draft RTA-compliant 14-day notices and 24-hour entry alerts without opening the CTS handbook.",
    faq: [
      { q: "Is there rent control in Alberta?", a: "No — Alberta has no cap on rent increases, though landlords must give at least 3 months written notice and can only raise once per year per tenant. Central Rentals still recommends benchmarking against local CMHC averages." },
      { q: "How fast is RTDRS in Alberta?", a: "Most Residential Tenancy Dispute Resolution Service hearings are scheduled within 21 days — much faster than court. Central Rentals bundles the required Statement of Claim documents for you." },
      { q: "What's the security deposit limit in Alberta?", a: "Max one month's rent. Must be held in an interest-bearing trust account; interest is paid annually per the posted rate." }
    ]
  },
  "quebec": {
    top_cities: ["montreal", "quebec-city", "laval", "gatineau", "sherbrooke", "trois-rivieres", "saguenay", "levis"],
    board: "Tribunal administratif du logement (TAL)",
    notice_days: 90,
    avg_rent: 1600,
    unique_hook: "Propriétaires québécois utilisent Central Rentals pour rédiger des baux TAL en français, gérer les avis d'augmentation Loi 41 et suivre les délais réglementaires.",
    faq: [
      { q: "Can I refuse to renew a lease in Quebec?", a: "Only for very narrow grounds (personal use, major renovation, subdivision) — otherwise Quebec leases auto-renew on the same terms. You must serve the tenant a formal reprise-de-logement or éviction notice through the TAL and often pay indemnité." },
      { q: "Est-ce que le formulaire de bail est obligatoire au Québec?", a: "Oui — le formulaire de bail obligatoire du TAL doit être utilisé pour toute location résidentielle. Central Rentals l'importe pré-rempli avec les clauses provinciales." },
      { q: "How much can I raise rent in Quebec?", a: "TAL publishes an annual estimation grid (0.7-1.9% for 2026 depending on building type + heating). Tenants can refuse an increase and force TAL arbitration." }
    ]
  },
  "manitoba": {
    top_cities: ["winnipeg", "brandon", "steinbach", "portage-la-prairie", "thompson", "winkler"],
    board: "Residential Tenancies Branch (RTB)",
    notice_days: 90,
    avg_rent: 1350,
    unique_hook: "Manitoba landlords rely on Central Rentals for the province's rent-increase guideline (with the RTB-published cap) and Form 4/Form 12 notices baked in.",
    faq: [
      { q: "What's the Manitoba rent-increase guideline for 2026?", a: "0% — Manitoba announced a 2026 rent freeze under the Residential Tenancies Act. Landlords can only raise rent above 0% by filing an above-guideline application with the RTB." },
      { q: "Who resolves landlord-tenant disputes in Manitoba?", a: "The Residential Tenancies Branch (RTB) under Manitoba Justice. Applications can be filed online; most hearings happen within 30 days." },
      { q: "How much notice does a Manitoba tenant need to give?", a: "One full rental period (one month for month-to-month) — the notice must be in writing and delivered by the last day of the previous month." }
    ]
  },
  "saskatchewan": {
    top_cities: ["saskatoon", "regina", "prince-albert", "moose-jaw", "swift-current", "yorkton"],
    board: "Office of Residential Tenancies (ORT)",
    notice_days: 60,
    avg_rent: 1250,
    unique_hook: "Saskatchewan owners use Central Rentals for ORT-ready 14-day payment notices and the exact SLTA-compliant statutory clauses.",
    faq: [
      { q: "How do I file a claim with the Office of Residential Tenancies?", a: "File online at gov.sk.ca — ORT hearings for arrears are typically scheduled within 14 days. Central Rentals prepares the ledger + notices bundle you'll need." },
      { q: "Is there rent control in Saskatchewan?", a: "No cap on the amount, but 6 months notice is required for any increase. Only one increase per 12 months per tenancy." },
      { q: "What's the security deposit limit in Saskatchewan?", a: "Max one month's rent. Must be returned within 7 business days of move-out, minus any documented damages beyond normal wear." }
    ]
  },
  "nova-scotia": {
    top_cities: ["halifax", "dartmouth", "sydney", "truro", "new-glasgow", "bridgewater"],
    board: "Residential Tenancies (RTO) — Service Nova Scotia",
    notice_days: 90,
    avg_rent: 1900,
    unique_hook: "Nova Scotia landlords run Central Rentals to stay inside the 5% rent-cap regulation and generate Form D notices without the RTO paperwork friction.",
    faq: [
      { q: "What's the Nova Scotia rent-cap for 2026?", a: "5% — extended through 2027 under the Residential Tenancies Act. Any increase above 5% is void. Central Rentals warns you before you enter a non-compliant amount." },
      { q: "How much notice must I give a Nova Scotia tenant?", a: "3 months (Form D notice) for landlord-use or end-of-term termination. Central Rentals auto-fills the form the day the trigger applies." },
      { q: "Where do Nova Scotia tenancy disputes go?", a: "The Residential Tenancies program at Service Nova Scotia; hearings can be by phone. Central Rentals bundles your evidence into an RT-ready PDF." }
    ]
  },
  "new-brunswick": {
    top_cities: ["fredericton", "saint-john", "moncton", "dieppe", "riverview", "miramichi"],
    board: "Residential Tenancies Tribunal (RTT)",
    notice_days: 90,
    avg_rent: 1350,
    unique_hook: "New Brunswick landlords use Central Rentals to generate RTT-compliant Form 8 notices and the 3-month rent-increase alerts required under the RTA.",
    faq: [
      { q: "Is there rent control in New Brunswick?", a: "No formal cap, but 3 months written notice is required for any increase and the tenant may petition RTT if the increase is 'unconscionable' — typically anything above regional CPI-linked benchmarks." },
      { q: "How does the Residential Tenancies Tribunal work?", a: "The RTT is a free service — file online, mediation usually happens within 15 days, and orders are enforceable in Small Claims Court." },
      { q: "How much can I charge for a security deposit in New Brunswick?", a: "Up to one full month's rent, held in trust and returned within 7 days of move-out." }
    ]
  },
  "newfoundland-and-labrador": {
    top_cities: ["st-johns", "mount-pearl", "corner-brook", "conception-bay-south", "grand-falls-windsor", "gander"],
    board: "Residential Tenancies Section (RTS)",
    notice_days: 90,
    avg_rent: 1200,
    unique_hook: "NL landlords use Central Rentals for RTS-ready notices, deposit-limit checks, and the province's slower winter-eviction timelines built into the workflow.",
    faq: [
      { q: "What's the security deposit limit in Newfoundland?", a: "Three-quarters of one month's rent — enforced by the Residential Tenancies Section. Central Rentals warns you if you enter more." },
      { q: "How do I file a rent-arrears claim in NL?", a: "File a Form 3 with the Residential Tenancies Section (via ServiceNL). Central Rentals exports the full lease + payment ledger as evidence." },
      { q: "Is there rent control in Newfoundland and Labrador?", a: "No cap on rent increases, but 8 weeks written notice is required for month-to-month and 3 months for fixed-term renewals." }
    ]
  },
  "prince-edward-island": {
    top_cities: ["charlottetown", "summerside", "stratford", "cornwall", "montague", "kensington"],
    board: "Island Regulatory and Appeals Commission (IRAC)",
    notice_days: 60,
    avg_rent: 1400,
    unique_hook: "PEI landlords rely on Central Rentals for IRAC-ready Form 4 notices and the province's mandatory rent-increase pre-approval workflow.",
    faq: [
      { q: "Is there rent control on Prince Edward Island?", a: "Yes — IRAC sets an annual allowable increase (3% for 2026). Any increase above must be pre-approved by IRAC before serving notice. Central Rentals blocks non-compliant amounts." },
      { q: "How much notice to raise rent on PEI?", a: "Minimum 3 months written notice using IRAC Form 12. Only one increase per 12 months per unit." },
      { q: "Where do PEI landlord-tenant disputes go?", a: "IRAC — the Island Regulatory and Appeals Commission hears every rental dispute. Central Rentals exports your case bundle in IRAC-compatible format." }
    ]
  },
  "yukon": {
    top_cities: ["whitehorse", "dawson-city", "watson-lake", "haines-junction"],
    board: "Residential Tenancies Office (RTO)",
    notice_days: 60,
    avg_rent: 1350,
    unique_hook: "Yukon landlords use Central Rentals for the territory's straightforward 30-day rent-arrears path and RLO-compliant lease templates.",
    faq: [
      { q: "How do I evict a tenant in Yukon for unpaid rent?", a: "Serve a 14-day notice to pay; if unpaid, file with the Residential Tenancies Office. Most hearings happen within 30 days." },
      { q: "Is there rent control in Yukon?", a: "No formal cap, but landlords must give 3 months written notice for any rent increase and can only raise once every 12 months." },
      { q: "What's the security deposit limit in Yukon?", a: "Up to one month's rent — must be returned within 15 days of move-out or a written accounting sent." }
    ]
  },
  "northwest-territories": {
    top_cities: ["yellowknife", "hay-river", "inuvik", "fort-smith", "behchoko"],
    board: "Rental Officer (Justice NT)",
    notice_days: 60,
    avg_rent: 1900,
    unique_hook: "NWT landlords lean on Central Rentals for territory-specific notice periods and the direct-to-Rental-Officer filing bundle.",
    faq: [
      { q: "Who handles NWT landlord-tenant disputes?", a: "The Rental Officer under Justice NT. Hearings are informal and can be held by phone; orders are enforceable in Territorial Court." },
      { q: "How much notice for NWT rent increase?", a: "Minimum 3 months, once per 12 months per unit. No published cap." },
      { q: "What documents do I need to evict for arrears?", a: "10-day pay-or-quit notice, then a filed application. Central Rentals prepares both in one click." }
    ]
  },
  "nunavut": {
    top_cities: ["iqaluit", "rankin-inlet", "arviat", "baker-lake", "cambridge-bay"],
    board: "Rental Office (Justice Nunavut)",
    notice_days: 60,
    avg_rent: 2300,
    unique_hook: "Nunavut landlords use Central Rentals for Inuit-language ready notice templates and the territory-specific dispute filing workflow.",
    faq: [
      { q: "How do rental disputes work in Nunavut?", a: "Applications are filed with the Rental Office (Justice Nunavut); hearings are usually held by phone. Central Rentals bundles the exhibits for you." },
      { q: "Is there rent control in Nunavut?", a: "No formal cap; landlords must serve 3 months written notice for any rent increase." },
      { q: "What's the security deposit rule in Nunavut?", a: "Up to one month's rent, returned within 10 days of move-out." }
    ]
  }
};

// Helper: humanise a city slug ("prince-albert" → "Prince Albert").
export const humaniseCity = (slug) =>
  (slug || "").split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
